/**
 * Delte deterministiske visuals per plass (eng-review D5): hash av spotId gir
 * stabile farger/former/seed på tvers av klistremerketavla (StickerBoard),
 * besøkslisten (VisitsTab) og reise-replayen (ReplayEngine/Canvas).
 * Algoritmene er flyttet VERBATIM fra komponentene — regresjonstesten i
 * test/spotVisuals.test.ts låser at fargene er identiske med før flyttingen.
 * Modulen er REN (ingen imports) og trygg å bruke fra replay-motorens graf.
 */

/** Felles hash: sum av char-koder. Samme uttrykk som lå duplisert i komponentene. */
export function hashSpotId(spotId: string): number {
  return spotId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
}

export interface RetroColorScheme {
  bg: string;        // tailwind gradient-klasser
  text: string;
  accent: string;
  label: string;
  canvasBg: [string, string];
}

const RETRO_SCHEMES: RetroColorScheme[] = [
  { bg: 'from-amber-600 to-yellow-600', text: '#291d03', accent: '#78350f', label: 'amber', canvasBg: ['#d97706', '#ca8a04'] },
  { bg: 'from-emerald-700 to-green-600', text: '#fcfbf7', accent: '#052e16', label: 'emerald', canvasBg: ['#047857', '#16a34a'] },
  { bg: 'from-orange-700 to-red-600', text: '#fcfbf7', accent: '#450a0a', label: 'rust', canvasBg: ['#c2410c', '#dc2626'] },
  { bg: 'from-teal-800 to-cyan-700', text: '#fcfbf7', accent: '#042f2e', label: 'teal', canvasBg: ['#115e59', '#0e7490'] },
  { bg: 'from-rose-800 to-pink-700', text: '#fcfbf7', accent: '#4c0519', label: 'burgundy', canvasBg: ['#9f1239', '#be185d'] },
  { bg: 'from-stone-700 to-neutral-600', text: '#fcfbf7', accent: '#1c1917', label: 'slate', canvasBg: ['#44403c', '#525252'] },
];

/** Deterministisk vintage-fargeskjema (klistremerker + replay-stopp). */
export function getRetroColors(spotId: string): RetroColorScheme {
  return RETRO_SCHEMES[hashSpotId(spotId) % RETRO_SCHEMES.length];
}

/** Deterministisk form + ikon for retro-klistremerker. */
export function getRetroShape(spotId: string): { shape: string; icon: string } {
  const shapes = ['circle', 'pill', 'hexagon'];
  const icons = ['camp', 'star', 'mountain', 'pine'];
  const hash = hashSpotId(spotId);
  return {
    shape: shapes[hash % shapes.length],
    icon: icons[(hash >> 2) % icons.length],
  };
}

/** Deterministisk PRNG fra streng-seed (mini-kart-detaljer m.m.). */
export function createSeededRandom(seedString: string): () => number {
  let h = 1540483477;
  for (let i = 0; i < seedString.length; i++) {
    h = Math.imul(h ^ seedString.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return function () {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
  };
}

const ACCENT_CLASSES = [
  'bg-emerald-700', 'bg-teal-700', 'bg-cyan-700',
  'bg-blue-700', 'bg-indigo-700', 'bg-violet-700',
  'bg-pink-700', 'bg-rose-700', 'bg-orange-700', 'bg-amber-700',
];

const ACCENT_HEX = ['#047857', '#0f766e', '#0e7490', '#1d4ed8', '#4338ca', '#7c3aed', '#be185d', '#be123c', '#c2410c', '#b45309'];

/** Deterministisk tailwind-aksentklasse per plass (besøkslisten). */
export function getSpotAccentColor(spotId: string): string {
  return ACCENT_CLASSES[hashSpotId(spotId) % ACCENT_CLASSES.length];
}

/** Deterministisk aksent-hex per plass (canvas/inline-styles). */
export function getAccentHex(spotId: string): string {
  return ACCENT_HEX[hashSpotId(spotId) % ACCENT_HEX.length];
}
